import { ChangeDetectionStrategy, Component, DestroyRef, Signal, inject, signal, computed } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import { ALL_NAV_ITEMS, EXCLUSIVE_ACCORDION, RAIL_WORLDS, RailWorld, RailSection } from '../nav-data.constant';
import { NavItem } from 'src/app/shared/api/types/interfaces';
import {
  BusinessRulesLocation,
  NavSectionEnum,
  NavSectionTranslations,
  NavValueTranslations,
  RoutePaths
} from 'src/app/shared/api/types/enums';
import { MixpanelService } from 'src/app/shared/services/mixpanel.service';
import * as CatalogSelectors from 'src/app/catalog/store/catalog.selectors';
import { RailStateService } from './rail-state.service';
import { RailTreeService } from '../rail-tree.service';
import { ChromeReadyService } from '../chrome-ready.service';

/**
 * Grace period before a collapsed-rail flyout closes on (mouseleave). Long
 * enough to survive the pointer clipping a corner on its way into the panel,
 * short enough that leaving the rail still feels immediate.
 */
const FLYOUT_CLOSE_DELAY_MS = 160;

/**
 * The Direction-C accordion rail (NBO-2331, canonical /mockups/shell/ia).
 * Dark in both themes; 224px expanded, 56px collapsed with a dark flyout.
 * Exclusive accordion behind the one-line EXCLUSIVE_ACCORDION flag; the
 * section holding the active route auto-expands on navigation; the accent
 * wash marks exactly ONE element — the active item row (the collapsed rail
 * washes the active section's icon instead, since the item row isn't
 * visible there). Collapse toggle lives at the rail FOOT.
 */
@Component({
  selector: 'app-shell-rail',
  standalone: true,
  imports: [RouterLink, TranslateModule, MatIconModule, MatTooltipModule],
  templateUrl: './shell-rail.component.html',
  styleUrls: ['./shell-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShellRailComponent {
  navSectionTranslations = NavSectionTranslations;
  navValueTranslations = NavValueTranslations;
  readonly productCataloguePath: string = RoutePaths.ProductCatalogue;

  // Collapse state lives in a shared service so the app frame can publish the
  // live rail width as --app-rail-width (see RailStateService).
  readonly collapsed = this.railState.collapsed;
  openSections = signal<ReadonlySet<NavSectionEnum>>(new Set());
  flyoutSection = signal<NavSectionEnum | null>(null);
  private flyoutCloseTimer?: ReturnType<typeof setTimeout>;
  private activePaths = signal<ReadonlySet<string>>(new Set());

  /** Gates the whole tree — see the note in the template (NBO-2331 r4). */
  private readonly chromeReady = inject(ChromeReadyService);
  readonly navReady = this.chromeReady.navReady;

  // The catalog listing restores its last filters when re-entered via nav
  // (carried over from the legacy side-nav).
  catalogQueryParams = toSignal(this.store.select(CatalogSelectors.selectCatalogQueryParams), {
    initialValue: {}
  });

  /**
   * Role- and env-gated tree. Owned by RailTreeService, not here: the top bar
   * resolves its section label from the SAME signal (NBO-2388), so the label
   * is the string this rail renders for every viewer — including a section
   * that roles collapse to a single link row.
   */
  readonly visibleWorlds: Signal<RailWorld[]> = this.railTree.visibleWorlds;

  /** The section holding the active route (drives auto-expand + strong header label). */
  activeSectionKey = computed<NavSectionEnum | null>(() => {
    const paths = this.activePaths();
    for (const world of RAIL_WORLDS) {
      for (const section of world.sections) {
        if (section.items.some((item) => paths.has(item.path))) {
          return section.key;
        }
      }
    }
    return null;
  });

  constructor(
    private railState: RailStateService,
    private store: Store,
    private router: Router,
    private railTree: RailTreeService,
    private mixpanelService: MixpanelService,
    private destroyRef: DestroyRef
  ) {
    this.applyUrl(this.router.url);
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((event) => this.applyUrl(event.urlAfterRedirects || event.url));

    // A pending close would fire into a destroyed view otherwise.
    this.destroyRef.onDestroy(() => this.cancelFlyoutClose());
  }

  isOpen(section: RailSection): boolean {
    return !this.collapsed() && this.openSections().has(section.key) && section.items.length > 1;
  }

  isSectionActive(section: RailSection): boolean {
    return this.activeSectionKey() === section.key;
  }

  isItemActive(item: NavItem): boolean {
    return this.activePaths().has(item.path);
  }

  toggleSection(section: RailSection): void {
    if (section.items.length <= 1) {
      return; // single-item sections are plain links, not accordions
    }
    this.openSections.update((current) => {
      if (current.has(section.key)) {
        const next = new Set(current);
        next.delete(section.key);
        return next;
      }
      // EXCLUSIVE_ACCORDION: the open set is replaced. Multi-open: additive.
      return EXCLUSIVE_ACCORDION ? new Set([section.key]) : new Set(current).add(section.key);
    });
  }

  toggleCollapse(): void {
    this.railState.toggle();
    this.closeFlyout();
  }

  showFlyout(section: RailSection): void {
    if (!this.collapsed()) {
      return;
    }
    // Re-entering during the grace period keeps the panel that is already open.
    this.cancelFlyoutClose();
    this.flyoutSection.set(section.key);
  }

  /**
   * Closes on a short delay rather than on the raw (mouseleave).
   *
   * The pointer legitimately leaves the icon cell on its way INTO the flyout —
   * the CSS bridges the gap between the two, but a fast or diagonal travel can
   * still register a frame outside both boxes, and an immediate close unmounted
   * the panel mid-approach (NBO-2329 review, Jasper). The delay is short enough
   * to feel instant when leaving the rail for real, and any re-entry cancels it.
   */
  hideFlyout(section: RailSection): void {
    if (this.flyoutSection() !== section.key) {
      return;
    }
    this.cancelFlyoutClose();
    this.flyoutCloseTimer = setTimeout(() => {
      this.flyoutCloseTimer = undefined;
      if (this.flyoutSection() === section.key) {
        this.flyoutSection.set(null);
      }
    }, FLYOUT_CLOSE_DELAY_MS);
  }

  /** Immediate close — a click has committed, so there is nothing to protect. */
  closeFlyout(): void {
    this.cancelFlyoutClose();
    this.flyoutSection.set(null);
  }

  private cancelFlyoutClose(): void {
    if (this.flyoutCloseTimer !== undefined) {
      clearTimeout(this.flyoutCloseTimer);
      this.flyoutCloseTimer = undefined;
    }
  }

  trackMenuClick(menu: string): void {
    this.mixpanelService.track(menu + ' click', {
      propertyOne: 'valueOne',
      propertyTwo: 'valueTwo'
    });
  }

  queryParamsFor(item: NavItem): Record<string, unknown> {
    return item.path === this.productCataloguePath ? this.catalogQueryParams() : {};
  }

  /**
   * Resolve the URL to the nav item that owns it by LONGEST PREFIX on a segment
   * boundary, with rule-builder routes mapped back to their owning listing.
   *
   * Prefix matching, not the legacy side-nav's fixed three-segment normalization
   * (which this replaces): a detail route carries an id, so
   * `/user-management/companies/123` normalized to three segments equals no nav
   * path at all — the item lost its mark and the section collapsed the moment
   * you opened a record from it. Longest-wins so `bo/orders` beats a shorter
   * sibling; a detail page keeps its parent lit, which is what the user expects
   * of a rail.
   */
  private applyUrl(url: string): void {
    const path = url.split('?')[0].split('#')[0].split('/').filter(Boolean).join('/');

    // Every rule type lives on the one Business rules screen (NBO-2446).
    const ruleBuilderContextToPath: { [context: string]: string } = Object.fromEntries(
      Object.values(BusinessRulesLocation).map((type) => [type, RoutePaths.SacBusinessRules])
    );

    const [first, second] = path.split('/');
    let matched = '';

    if (first === 'rule-builder' && second) {
      matched = ruleBuilderContextToPath[second] || '';
    } else {
      for (const item of ALL_NAV_ITEMS) {
        const candidate = item.path;
        if (!candidate) {
          continue;
        }
        // Segment boundary: 'orders' must not match '/orders-export/1'.
        const isPrefix = path === candidate || path.startsWith(candidate + '/');
        if (isPrefix && candidate.length > matched.length) {
          matched = candidate;
        }
      }
    }

    this.activePaths.set(matched ? new Set([matched]) : new Set());

    // The section holding the active route auto-expands (replacing the open
    // set under the exclusive flag, adding to it otherwise).
    const activeKey = this.activeSectionKey();
    if (activeKey) {
      this.openSections.update((current) =>
        EXCLUSIVE_ACCORDION ? new Set([activeKey]) : new Set(current).add(activeKey)
      );
    }
  }
}
